import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, ButtonContainer, Layout } from '@vwfs-bronson/bronson-react';
import { Formik } from 'formik';
import { DatePickerField } from '../../../../components/DatePickerField';
import { FormField } from '../../../../components/FormField/FormField';
import { SearchableSelectFormField } from '../../../../components/FormFields/SearchableSelect';
import { DealerDashboardSubpage } from '../../../../resources/base/models/enums';
import { dashboardSelectors } from '../../../../services/redux/features/dashboard.slice';
import { DropdownSearchFields, Search } from '../../fieldset';
import { StatusFilterCombobox } from '../../StatusFilterCombobox';
import { RunningContractStatusType } from '../utils/searchedValues.types';
import { getFormInitialValues } from './initial-values';
import { DashBoardSubpageSchema } from './validationSchema';

type ComboboxOption = { value: string; label: string };

interface DashBoardSearchSectionProps {
  fields?: string[];
  searchQuotes: (values: Record<string, unknown>) => void;
  subpage?: DealerDashboardSubpage;
  searchQuery?: boolean;
  salesPersonDropdown?: ComboboxOption[];
  searchFieldFinancialProductDropdownValues?: ReadonlyArray<{ value: string; label: string }>;
  defaultSalesPerson?: string;
}

export const DashBoardSearchSection: FunctionComponent<DashBoardSearchSectionProps> = ({
  fields,
  searchQuery = false,
  searchQuotes,
  subpage,
  salesPersonDropdown,
  searchFieldFinancialProductDropdownValues,
  defaultSalesPerson,
}) => {
  const { t } = useTranslation();
  const savedSearchForm = useSelector(dashboardSelectors.selectDashboardSearchFormWithSelections);
  const runningContractStatus: RunningContractStatusType | undefined = useSelector(
    dashboardSelectors.selectRunningContractStatus
  );
  const [initialValues, setInitialValues] = useState({} as any);

  useEffect(() => {
    setInitialValues({
      ...getFormInitialValues(savedSearchForm, defaultSalesPerson),
      ...(runningContractStatus && { runningContractStatus }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <Layout.Item default="1/1">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={DashBoardSubpageSchema(subpage)}
          onSubmit={(values) => searchQuotes(values)}
        >
          {(formProps) => {
            const { errors, submitForm, setFieldValue, values } = formProps;
            const isValid = Object.keys(errors).length === 0;
            const salesPersonOptions = (salesPersonDropdown || []).map((option) => ({
              key: option.value,
              value: option.label,
            }));

            const salesPersonOptionsWithDefault = [{ key: '', value: 'Sélectionner Tous' }, ...salesPersonOptions];
            const selectedSalesPerson = salesPersonOptionsWithDefault.filter(({ key }) => key === values.salesPerson);
            return (
              <>
                <Layout className="u-pt-none">
                  <Layout.Item>
                    <Layout>
                      {searchQuery && (
                        <Layout.Item default="1/1" className="u-mb u-mt">
                          <FormField
                            name="searchQuery"
                            type="input"
                            labelText={t('dashboard:search:fields:labelSearchBar')}
                            hint={t('dashboard:search:fields:hintSearchBar')}
                            render={(fieldProps) => (
                              <Search {...fieldProps} onSearch={(value) => searchQuotes(value)} />
                            )}
                          />
                        </Layout.Item>
                      )}
                    </Layout>
                    <Layout>
                      {fields?.includes('statusId') && (
                        <Layout.Item s="1/1">
                          <StatusFilterCombobox
                            name="statusId"
                            label={t('dashboard:search:fields:statusId')}
                            subpage={subpage!}
                          />
                        </Layout.Item>
                      )}
                      {fields?.includes('salesPerson') && (
                        <Layout.Item default="1/3" s="1/1">
                          {salesPersonDropdown && salesPersonDropdown.length <= 10 && (
                            <DropdownSearchFields
                              name="salesPerson"
                              label={t('dashboard:search:fields:salesPerson:label')}
                              list={salesPersonDropdown || []}
                              defaultValueLabel={t('dashboard:search:fields:salesPerson:searchAllOption')}
                            />
                          )}

                          {salesPersonDropdown && salesPersonDropdown.length > 10 && (
                            <SearchableSelectFormField
                              id="salesPerson"
                              name="salesPerson"
                              options={salesPersonOptionsWithDefault}
                              value={selectedSalesPerson}
                              noOptionsMessage={t('outletSearchCombobox:searchList:noOptionsMessage')}
                              placeholder={t('app:dropdown:placeholder')}
                              searchInputLabel={t('dashboard:search:fields:salesPerson:label')}
                              onChange={(selectedOptions) => {
                                const selectedSalesPersonValue = selectedOptions[0]?.key ?? '';
                                setFieldValue('salesPerson', selectedSalesPersonValue);
                              }}
                            />
                          )}
                        </Layout.Item>
                      )}
                      {fields?.includes('runningContractStatus') && (
                        <Layout.Item default="1/2" s="1/1">
                          <DropdownSearchFields
                            name="runningContractStatus"
                            label={t('dashboard:search:fields:runningContractStatus')}
                            list={[
                              { label: 'Payé', value: RunningContractStatusType.PAID },
                              { label: 'Activé', value: RunningContractStatusType.ACTIVE },
                            ]}
                          />
                        </Layout.Item>
                      )}

                      {fields?.includes('creationDate') && (
                        <Layout.Item default="1/3" s="1/1">
                          <DatePickerField
                            name="creationDate"
                            label={t('dashboard:search:fields:creationDate')}
                            mode="range"
                          />
                        </Layout.Item>
                      )}
                      {fields?.includes('financialProduct') && (
                        <Layout.Item default="1/3" s="1/1">
                          <DropdownSearchFields
                            name="financialProduct"
                            label={t('dashboard:search:fields:financialProduct')}
                            list={searchFieldFinancialProductDropdownValues}
                          />
                        </Layout.Item>
                      )}
                      <Layout.Item default="1/1">
                        <ButtonContainer right nav>
                          <Button type="button" onClick={submitForm} disabled={!isValid}>
                            {t('dashboard:search:button')}
                          </Button>
                        </ButtonContainer>
                      </Layout.Item>
                    </Layout>
                  </Layout.Item>
                </Layout>
              </>
            );
          }}
        </Formik>
      </Layout.Item>
    </Layout>
  );
};
